// Canned replies: saved texts the composer inserts with a "/" shortcut.
// Every role reads them; only owners and admins change the list, and the
// server enforces that. Inserting one sends nothing: the user still
// presses Send, so the message is billed like any typed reply.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, unwrap } from './client'
import type { components } from './generated/schema'

export type CannedReply = components['schemas']['CannedReply']
export type CannedReplyInput = components['schemas']['CannedReplyInput']

const key = ['canned-replies'] as const

export function useCannedReplies() {
  return useQuery({
    queryKey: key,
    queryFn: async (): Promise<CannedReply[]> => unwrap(await api.GET('/canned-replies')),
    staleTime: 60_000,
  })
}

function useRefresh() {
  const qc = useQueryClient()
  return () => void qc.invalidateQueries({ queryKey: key })
}

export function useCreateCannedReply() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (input: CannedReplyInput) => unwrap(await api.POST('/canned-replies', { body: input })),
    onSuccess: refresh,
  })
}

export function useUpdateCannedReply() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (input: { id: string } & CannedReplyInput) =>
      unwrap(
        await api.PATCH('/canned-replies/{cannedReplyId}', {
          params: { path: { cannedReplyId: input.id } },
          body: { shortcut: input.shortcut, body: input.body },
        }),
      ),
    onSuccess: refresh,
  })
}

export function useDeleteCannedReply() {
  const refresh = useRefresh()
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await api.DELETE('/canned-replies/{cannedReplyId}', { params: { path: { cannedReplyId: id } } })
      if (!res.response.ok) unwrap(res)
    },
    onSuccess: refresh,
  })
}

// The text a canned reply becomes for one customer. {{name}} is the only
// placeholder; anything else stays as typed.
export function fillCannedReply(body: string, customer: string): string {
  return body.replaceAll('{{name}}', customer.trim() || 'there')
}

// Replies whose shortcut starts with the typed prefix, case ignored.
export function matchCannedReplies(list: CannedReply[], prefix: string): CannedReply[] {
  const p = prefix.toLowerCase()
  return list.filter((c) => c.shortcut.toLowerCase().startsWith(p))
}
